<template>
  <div class="pos-cart">
    <!-- Fixed position tooltip that escapes overflow -->
    <div
      v-if="tooltipVisible"
      class="fixed-tooltip"
      :style="tooltipStyle"
    >
      Part of a deal - additional discounts do not apply
    </div>
    <div class="product-table">
      <!-- Grouped Items by Discount Type -->
      <div
        v-for="group in groupedItemsMeta"
        :key="group.key"
        class="discount-group"
        :class="{ 'discount-group--collapsed': collapsedGroups[group.key] }"
      >
        <!-- Group Header -->
        <div
          class="discount-group__header"
          :class="[`discount-group__header--${group.key}`]"
          @click="toggleGroup(group.key)"
        >
          <div class="discount-group__header-left">
            <i :class="['fas', group.icon, 'discount-group__icon']"></i>
            <span class="discount-group__label">{{ group.label }}</span>
            <span class="discount-group__count">
              <template v-if="group.key === 'bundle' && group.bundleCount > 0">
                {{ group.bundleCount }} {{ group.bundleCount === 1 ? 'bundle' : 'bundles' }} | {{ group.totalQuantity }} {{ group.totalQuantity === 1 ? 'item' : 'items' }}
              </template>
              <template v-else>
                {{ group.productCount }} {{ group.productCount === 1 ? 'product' : 'products' }} | {{ group.totalQuantity }} {{ group.totalQuantity === 1 ? 'item' : 'items' }}
              </template>
            </span>
          </div>
          <div class="discount-group__header-right">
            <span v-if="group.totalDiscount > 0" class="discount-group__pricing">
              <span class="discount-group__original">{{ formatCentsToPrice(group.totalOriginal) }}</span>
              <span class="discount-group__discount">-{{ formatCentsToPrice(group.totalDiscount) }}</span>
              <span class="discount-group__final">{{ formatCentsToPrice(group.totalFinal) }}</span>
            </span>
            <span v-else class="discount-group__final">
              {{ formatCentsToPrice(group.totalOriginal) }}
            </span>
            <i :class="['fas', collapsedGroups[group.key] ? 'fa-chevron-down' : 'fa-chevron-up', 'discount-group__chevron']"></i>
          </div>
        </div>

        <!-- Group Items (collapsible) -->
        <transition
          name="collapse"
          @before-enter="beforeEnter"
          @enter="enter"
          @after-enter="afterEnter"
          @before-leave="beforeLeave"
          @leave="leave"
        >
          <div v-show="!collapsedGroups[group.key]" class="discount-group__items">
            <div
              v-for="(item, index) in group.items"
              :key="`${item.productId}-${item.itemId}-${index}`"
              class="row elevated-1 cart-item pointer"
              :class="{ 'pulse-background': item.isLoading, 'is-cannabis': item.isCannabis }"
              @click="() => handleItemClick(item)"
            >
              <div class="cell-header">
                <b>{{ item.productName }}</b>
                <span v-if="item.batchId" class="batch-id text-color-1 text-size-1">
                  Batch #{{ item.batchId }}
                </span>
              </div>

              <div class="cell flex">
                <div style="flex: 1">
                  <span :class="{ discounted: isDiscounted(item) }">
                    {{ formatCentsToPrice(item.originalUnitPrice) }}
                  </span>
                  <span class="text-color-1">x</span>
                  {{ item.quantity }}
                </div>
                <div>
                  <span class="text-color-1 text-thin">Sub Total </span>
                  <span :class="{ discounted: isDiscounted(item) }">
                    {{ formatCentsToPrice(item.originalSubTotal) }}
                  </span>
                </div>
              </div>

              <div class="cell flex" v-if="isDiscounted(item)">
                <div style="flex: 1">
                  <div class="value-discounted">
                    {{ formatCentsToPrice(item.discountedUnitPrice) }}
                  </div>
                </div>
                <div class="value-discounted">
                  {{ formatCentsToPrice(item.discountedSubTotal) }}
                </div>
              </div>

              <div class="cell text-size-1 product-meta-row" v-if="(item.isCannabis && item.cannabisGramsUnit > 0) || getSupplierName(item) || getGroupName(item) || item.totalDiscount > 0 || hasQualifyingItemMarker(item)">
                <span class="text-color-1 product-meta-left">
                  <span v-if="getSupplierName(item)">{{ getSupplierName(item) }}</span>
                  <span v-if="getSupplierName(item) && getGroupName(item)" class="meta-separator">|</span>
                  <span v-if="getGroupName(item)">{{ getGroupName(item) }}</span>
                  <span v-if="(getSupplierName(item) || getGroupName(item)) && item.isCannabis && item.cannabisGramsUnit > 0" class="meta-separator">|</span>
                  <span v-if="item.isCannabis && item.cannabisGramsUnit > 0">
                    <i class="fas fa-leaf"></i>
                    {{ item.cannabisGramsUnit }}g
                  </span>
                </span>
                <span
                  v-if="hasQualifyingItemMarker(item) && item.totalDiscount === 0"
                  class="stacking-indicator"
                  @mouseenter="showTooltip($event)"
                  @mouseleave="hideTooltip"
                >
                  <i class="fas fa-info-circle"></i>
                </span>
                <span v-else-if="item.totalDiscount > 0" class="discount-total-inline">
                  <span class="discount-label">Total Discount</span>
                  -{{ formatCentsToPrice(item.totalDiscount) }}
                </span>
              </div>

              <!-- Discounts Applied Section -->
              <div v-if="item.discountsApplied && getFilteredDiscounts(item.discountsApplied).length > 0" class="discounts-applied">
                <div
                  v-for="(discount, dIndex) in getFilteredDiscounts(item.discountsApplied)"
                  :key="'discount-' + dIndex"
                  class="discount-applied-row"
                >
                  <span class="discount-rule-name">
                    <i class="fas fa-tag"></i>
                    {{ discount.ruleName }}
                    <span class="discount-effect">{{ formatEffectType(discount.effectType, discount.effectValue) }}</span>
                  </span>
                  <span class="discount-type-badge">{{ formatDiscountType(discount.discountType) }}</span>
                  <span class="discount-amount">-{{ formatCentsToPrice(discount.totalDiscount) }}</span>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- Rounding Adjustment Card -->
      <div v-if="roundingAdjustment && roundingAdjustment !== 0" class="rounding-adjustment-card">
        <div class="rounding-content">
          <span class="rounding-label">
            Rounding Adjustment
          </span>
          <span class="rounding-amount" :class="{ 'round-up': roundingAdjustment > 0, 'round-down': roundingAdjustment < 0 }">
            {{ formatRoundingDisplay(roundingAdjustment) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { discountTypeLabelMap } from "@/constants/discountRule";

export default {
  name: "PosCartItemized",
  components: {},
  data() {
    return {
      discountTypeLabelMap,
      tooltipVisible: false,
      tooltipStyle: {},
      collapsedGroups: {
        bundle: false,
        salePricing: false,
        otherDiscounts: false,
        noDiscount: false
      }
    };
  },
  props: {
    order: {
      type: Object,
      default: () => {
        return undefined;
      },
    },
  },
  computed: {
    orderItemsMeta() {
      if (!this.order?.itemsMeta) return [];
      return [...this.order.itemsMeta];
    },
    roundingAdjustment() {
      return this.order?.roundingAdjustment || 0;
    },
    groupedItemsMeta() {
      if (!this.order?.itemsMeta) return [];

      const groups = {
        bundle: { label: 'Bundle Deals', icon: 'fa-cubes', items: [], totalDiscount: 0, totalOriginal: 0, totalFinal: 0, bundleRuleIds: new Set(), productIds: new Set(), totalQuantity: 0 },
        salePricing: { label: 'Sale Pricing', icon: 'fa-percent', items: [], totalDiscount: 0, totalOriginal: 0, totalFinal: 0, productIds: new Set(), totalQuantity: 0 },
        otherDiscounts: { label: 'Other Discounts', icon: 'fa-tag', items: [], totalDiscount: 0, totalOriginal: 0, totalFinal: 0, productIds: new Set(), totalQuantity: 0 },
        noDiscount: { label: 'Regular Items', icon: 'fa-shopping-cart', items: [], totalDiscount: 0, totalOriginal: 0, totalFinal: 0, productIds: new Set(), totalQuantity: 0 }
      };

      for (const item of this.order.itemsMeta) {
        const primaryDiscount = item.discountsApplied?.find(d =>
          d.effectType !== 'QUALIFYING_ITEM' && d.discountType !== 'rounding'
        );

        if (!primaryDiscount) {
          groups.noDiscount.items.push(item);
          groups.noDiscount.totalOriginal += item.originalSubTotal || 0;
          groups.noDiscount.totalFinal += item.discountedSubTotal || item.originalSubTotal || 0;
          groups.noDiscount.productIds.add(item.productId?.toString());
          groups.noDiscount.totalQuantity += item.quantity || 1;
        } else if (primaryDiscount.discountType === 'bundle') {
          groups.bundle.items.push(item);
          groups.bundle.totalDiscount += item.totalDiscount || 0;
          groups.bundle.totalOriginal += item.originalSubTotal || 0;
          groups.bundle.totalFinal += item.discountedSubTotal || item.originalSubTotal || 0;
          groups.bundle.productIds.add(item.productId?.toString());
          groups.bundle.totalQuantity += item.quantity || 1;
          const bundleRuleId = primaryDiscount.ruleId?.toString();
          if (bundleRuleId) {
            groups.bundle.bundleRuleIds.add(bundleRuleId);
          }
        } else if (primaryDiscount.discountType === 'salePricing' ||
                   primaryDiscount.discountType === 'salePrice' ||
                   primaryDiscount.discountType === 'specialPricing') {
          groups.salePricing.items.push(item);
          groups.salePricing.totalDiscount += item.totalDiscount || 0;
          groups.salePricing.totalOriginal += item.originalSubTotal || 0;
          groups.salePricing.totalFinal += item.discountedSubTotal || item.originalSubTotal || 0;
          groups.salePricing.productIds.add(item.productId?.toString());
          groups.salePricing.totalQuantity += item.quantity || 1;
        } else {
          groups.otherDiscounts.items.push(item);
          groups.otherDiscounts.totalDiscount += item.totalDiscount || 0;
          groups.otherDiscounts.totalOriginal += item.originalSubTotal || 0;
          groups.otherDiscounts.totalFinal += item.discountedSubTotal || item.originalSubTotal || 0;
          groups.otherDiscounts.productIds.add(item.productId?.toString());
          groups.otherDiscounts.totalQuantity += item.quantity || 1;
        }
      }

      return Object.entries(groups)
        .filter(([key, group]) => group.items.length > 0)
        .map(([key, group]) => ({
          key,
          ...group,
          bundleCount: group.bundleRuleIds?.size || 0,
          productCount: group.productIds?.size || 0,
          totalQuantity: group.totalQuantity || 0
        }));
    },
  },
  methods: {
    formatCentsToPrice(cents) {
      if (cents === undefined || cents === null) return "$0.00";
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(cents / 100);
    },
    isDiscounted(item) {
      return item.totalDiscount > 0 || item.originalUnitPrice > item.discountedUnitPrice;
    },
    formatDiscountType(type) {
      if (!type) return "";
      return discountTypeLabelMap[type] || type;
    },
    formatEffectType(effectType, effectValue) {
      if (!effectType) return "";
      switch (effectType) {
        case "PERCENT_OFF":
          return `${effectValue}% off`;
        case "DOLLAR_OFF":
          return `$${(effectValue / 100).toFixed(2)} off`;
        case "FIXED_PRICE":
          return `Fixed $${(effectValue / 100).toFixed(2)}`;
        case "FREE":
          return "Free";
        case "ROUNDING":
          return "";
        default:
          return effectType;
      }
    },
    formatRoundingAmount(cents) {
      // Rounding adjustment: negative = savings (round down), positive = round up
      if (cents < 0) {
        return "-" + this.formatCentsToPrice(Math.abs(cents));
      }
      return "+" + this.formatCentsToPrice(cents);
    },
    formatRoundingDisplay(amount) {
      const formatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(Math.abs(amount));
      return amount > 0 ? `+${formatted}` : `-${formatted}`;
    },
    getProductForItem(itemMeta) {
      if (!this.order?.products) return null;
      return this.order.products.find(
        p => p._id?.toString() === itemMeta.productId?.toString()
      );
    },
    handleItemClick(itemMeta) {
      const product = this.getProductForItem(itemMeta);
      if (product) {
        this.$emit('select-product', product);
      }
    },
    hasQualifyingItemMarker(item) {
      return item.discountsApplied?.some(d => d.effectType === 'QUALIFYING_ITEM');
    },
    getSupplierName(item) {
      const product = this.getProductForItem(item);
      return product?.externalFields?.supplier?.name || '';
    },
    getGroupName(item) {
      const product = this.getProductForItem(item);
      return product?.externalFields?.group?.name || '';
    },
    showTooltip(event) {
      const rect = event.target.getBoundingClientRect();
      this.tooltipStyle = {
        top: `${rect.bottom + 8}px`,
        left: `${rect.right}px`,
        transform: 'translateX(-100%)',
      };
      this.tooltipVisible = true;
    },
    hideTooltip() {
      this.tooltipVisible = false;
    },
    getFilteredDiscounts(discountsApplied) {
      if (!discountsApplied) return [];
      // Filter out:
      // 1. QUALIFYING_ITEM effects (these are markers, not actual discounts)
      // 2. Rounding adjustments (rounding is treated as a payment, not a discount)
      return discountsApplied.filter(d =>
        d.effectType !== 'QUALIFYING_ITEM' && d.discountType !== 'rounding'
      );
    },
    toggleGroup(groupKey) {
      this.collapsedGroups[groupKey] = !this.collapsedGroups[groupKey];
    },
    beforeEnter(el) {
      el.style.height = '0';
      el.style.opacity = '0';
      el.style.overflow = 'hidden';
    },
    enter(el, done) {
      el.style.transition = 'height 0.25s ease, opacity 0.25s ease';
      requestAnimationFrame(() => {
        el.style.height = el.scrollHeight + 'px';
        el.style.opacity = '1';
      });
      setTimeout(done, 250);
    },
    afterEnter(el) {
      el.style.height = '';
      el.style.overflow = '';
      el.style.transition = '';
    },
    beforeLeave(el) {
      el.style.height = el.scrollHeight + 'px';
      el.style.overflow = 'hidden';
    },
    leave(el, done) {
      el.style.transition = 'height 0.25s ease, opacity 0.25s ease';
      requestAnimationFrame(() => {
        el.style.height = '0';
        el.style.opacity = '0';
      });
      setTimeout(done, 250);
    },
  },
};
</script>

<style scoped lang="scss">
@import "./_posCart.scss";

.cart-item {
  border-radius: 3px;
}

.is-cannabis {
  border-left: 3px solid #27ae60;
}

.batch-id {
  font-weight: normal;
}

.product-meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.product-meta-left {
  display: flex;
  align-items: center;
  gap: 4px;
}

.meta-separator {
  margin: 0 4px;
  opacity: 0.5;
}

.discount-total-inline {
  color: #f39c12;
  font-weight: 600;
  font-size: 1.25em;

  .discount-label {
    font-size: 0.75em;
    font-weight: normal;
    margin-right: 4px;
  }
}

.stacking-indicator {
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.25em;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }
}

.fixed-tooltip {
  position: fixed;
  z-index: 10000;
  background-color: #5c5c5e;
  color: #fff;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.85em;
  white-space: nowrap;
  pointer-events: none;
}

:global(.light) .fixed-tooltip {
  background-color: #444;
  color: #fff;
}

.light .stacking-indicator {
  color: rgba(0, 0, 0, 0.5);

  &:hover {
    color: rgba(0, 0, 0, 0.8);
  }
}

.discounts-applied {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed rgba(255, 255, 255, 0.1);
}

.light .discounts-applied {
  border-top-color: rgba(0, 0, 0, 0.1);
}

.discount-applied-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 0.85em;

  &:last-child {
    margin-bottom: 0;
  }
}

.discount-rule-name {
  flex: 1;
  color: #f39c12;

  i {
    margin-right: 4px;
  }
}

.discount-type-badge {
  background-color: rgba(243, 156, 18, 0.2);
  color: #f39c12;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.85em;
}

.discount-effect {
  color: $dark-text-color-1;
  font-style: italic;
}

.light .discount-effect {
  color: $light-text-color-1;
}

.discount-amount {
  color: #27ae60;
  font-weight: 600;
}

.rounding-adjustment-card {
  margin-top: 8px;
  padding: 8px 12px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  font-size: 0.85em;
}

.light .rounding-adjustment-card {
  background-color: rgba(0, 0, 0, 0.03);
}

.rounding-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.rounding-amount {
  font-weight: 600;

  &.round-up {
    color: #e74c3c;
  }

  &.round-down {
    color: #27ae60;
  }
}

// Discount Group Styles
.discount-group {
  margin-bottom: 12px;
  border-radius: 6px;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);

  &--collapsed {
    .discount-group__header {
      border-radius: 6px;
    }
  }
}

.light .discount-group {
  background-color: rgba(0, 0, 0, 0.02);
  border-color: rgba(0, 0, 0, 0.08);
}

.discount-group__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }

  &--bundle {
    background-color: rgba(155, 89, 182, 0.15);
    border-left: 3px solid #9b59b6;
  }

  &--salePricing {
    background-color: rgba(243, 156, 18, 0.15);
    border-left: 3px solid #f39c12;
  }

  &--otherDiscounts {
    background-color: rgba(52, 152, 219, 0.15);
    border-left: 3px solid #3498db;
  }

  &--noDiscount {
    background-color: rgba(149, 165, 166, 0.1);
    border-left: 3px solid #95a5a6;
  }
}

.light .discount-group__header {
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
}

.discount-group__header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.discount-group__header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.discount-group__icon {
  font-size: 0.9em;
  opacity: 0.8;
}

.discount-group__label {
  font-weight: 600;
  font-size: 0.9em;
}

.discount-group__count {
  background-color: rgba(255, 255, 255, 0.15);
  color: inherit;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.75em;
  font-weight: 600;
}

.light .discount-group__count {
  background-color: rgba(0, 0, 0, 0.1);
}

.discount-group__total {
  color: #27ae60;
  font-weight: 600;
  font-size: 0.9em;
}

.discount-group__pricing {
  display: flex;
  align-items: center;
  gap: 8px;
}

.discount-group__original {
  text-decoration: line-through;
  opacity: 0.6;
}

.discount-group__discount {
  color: #f39c12;
  font-weight: 600;
}

.discount-group__final {
  color: #27ae60;
  font-weight: 600;
}

.discount-group__chevron {
  font-size: 0.75em;
  opacity: 0.6;
  transition: transform 0.2s ease;
}

.discount-group__items {
  padding: 8px;
}
</style>

